// ============================================================================
// What a project's Ready-to-Invoice gate reads — ONE copy, used by the project
// screen (project-detail.tsx) and by Ask AI's project card (item #944, the
// projects part). Moved out of the page unchanged so the two cannot compute a
// project two ways: the lines are the authority for money and dates once a
// project has any (Run C Phase 2); a NULL derivation never stands in for a
// stored amount or completion date; the gate's words come from advance-gate.ts.
// ============================================================================

import { toNumOrNull } from '../../../shared/coerce';
import { OTHER_SERVICE_CODE } from '../hooks/use-service-catalog';
import { getAdvanceBlockers, type AdvanceGateFields } from './advance-gate';
import { deriveAmounts, deriveDates, lineIsSaveable, type EntryLine } from './entry-derive';
import { serviceLineToEntryLine } from './line-edit';
import { projectServiceLines } from './project-service-label';

/** The editable project fields, as strings for input binding. */
export interface EditState {
  notes: string;
  bill_amount: string;
  pay_amount: string;
  special_pay: boolean;
  reason_note: string;
  start_date: string;
  due_date: string;
  completion_date: string;
  assigned_partner_id: string;
  awaiting_schedule: boolean;
}

export interface EditableRow {
  notes: string | null;
  bill_amount: number | null;
  pay_amount: number | null;
  special_pay?: boolean | null;
  reason_note?: string | null;
  start_date: string | null;
  due_date: string | null;
  completion_date: string | null;
  assigned_partner_id: string | null;
  awaiting_schedule?: boolean | null;
}

export function rowToEditState(row: EditableRow): EditState {
  return {
    notes: row.notes ?? '',
    bill_amount: row.bill_amount != null ? String(row.bill_amount) : '',
    pay_amount: row.pay_amount != null ? String(row.pay_amount) : '',
    special_pay: row.special_pay ?? false,
    reason_note: row.reason_note ?? '',
    start_date: row.start_date ?? '',
    due_date: row.due_date ?? '',
    completion_date: row.completion_date ?? '',
    assigned_partner_id: row.assigned_partner_id ?? '',
    awaiting_schedule: row.awaiting_schedule ?? false,
  };
}

export interface GateProject extends EditableRow {
  project_services?: AdvanceGateFields['project_services'];
  event_class?: string | null;
}

/** Everything the screen shows about the project's money and dates, and the gate fields. */
export function readyGate(data: GateProject | null | undefined, edit: EditState | null | undefined, lines: EntryLine[]) {
  const saveableLines = lines.filter((l) => lineIsSaveable(l, OTHER_SERVICE_CODE));
  const hasLines = saveableLines.length > 0;
  const derivedAmounts = deriveAmounts(saveableLines);
  const derivedDates = deriveDates(saveableLines);

  // What the project row will actually carry after a save.
  const effectiveBill = hasLines ? derivedAmounts.bill_amount : toNumOrNull(edit?.bill_amount ?? '');
  const effectivePay = hasLines ? derivedAmounts.pay_amount : toNumOrNull(edit?.pay_amount ?? '');
  const effectiveCompletion = hasLines ? derivedDates.completion_date : (edit?.completion_date || null);

  // A derived NULL ("not every line carries this yet") never replaces a stored value.
  const billStands = hasLines && derivedAmounts.bill_amount === null && data?.bill_amount != null;
  const payStands = hasLines && derivedAmounts.pay_amount === null && data?.pay_amount != null;
  const completionStands = hasLines && derivedDates.completion_date === null && !!data?.completion_date;

  const billAmtNum = billStands ? (data?.bill_amount ?? null) : effectiveBill;
  const payAmtNum = payStands ? (data?.pay_amount ?? null) : effectivePay;
  const readyCompletion = completionStands ? (data?.completion_date ?? null) : effectiveCompletion;

  const gateFields: AdvanceGateFields = {
    completion_date: readyCompletion,
    bill_amount: billAmtNum,
    pay_amount: payAmtNum,
    project_services: data?.project_services ?? null,
    event_class: data?.event_class ?? null,
  };
  return {
    saveableLines, hasLines, derivedAmounts, derivedDates,
    effectiveBill, effectivePay, effectiveCompletion,
    billStands, payStands, completionStands,
    billAmtNum, payAmtNum, readyCompletion,
    gateFields,
  };
}

/** A saved project's blockers to Ready to Invoice, exactly as its screen shows them with nothing edited. */
export function savedProjectBlockers(data: GateProject & Parameters<typeof projectServiceLines>[0]): string[] {
  const lines = projectServiceLines(data).map((row, i) => serviceLineToEntryLine(row, `saved-${row.service_line_id}-${i}`));
  return getAdvanceBlockers(readyGate(data, rowToEditState(data), lines).gateFields);
}
